import React, { useState } from "react";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

const NAV = [
  { key: "nav_home", hash: "" },
  { key: "nav_programs", hash: "#programs" },
  { key: "nav_education", hash: "#education" },
  { key: "nav_advisors", hash: "#advisors" },
  { key: "nav_contact", hash: "#contact" },
];

const Header = () => {
  const { lang, t } = useT();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const home = `/${lang}`;
  const otherLang = lang === "mx" ? "en" : "mx";

  const switchLang = () => {
    // Replace language segment, keep the rest of the path
    const parts = location.pathname.split("/").filter(Boolean);
    if (parts[0] === "mx" || parts[0] === "en") {
      parts[0] = otherLang;
    } else {
      parts.unshift(otherLang);
    }
    navigate("/" + parts.join("/") + location.hash, { replace: false });
  };

  const goToSection = (hash) => {
    setOpen(false);
    if (!hash) {
      // Home link — go to home page top
      if (location.pathname === home && location.hash === "") {
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      } else {
        navigate(home);
      }
      return;
    }
    if (location.pathname === home) {
      // Same page: smooth-scroll and sync the hash (without adding history)
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      window.history.replaceState(null, "", home + hash);
    } else {
      // Different page: navigate with hash — ScrollToTop will scroll to the block
      navigate(home + hash);
    }
  };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 w-full border-b border-slate-200 bg-white shadow-sm"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link
          to={home}
          data-testid="header-logo-link"
          className="flex items-center gap-2"
        >
          <span
            aria-hidden="true"
            className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-[#0B2545] font-serif text-lg font-bold text-white"
          >
            F
          </span>
          <span className="font-serif text-lg font-bold tracking-tight text-slate-900">
            {t("brand.name")}
          </span>
        </Link>

        {/* Desktop nav */}
        <nav
          data-testid="header-desktop-nav"
          className="hidden items-center gap-7 md:flex"
        >
          {NAV.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => goToSection(item.hash)}
              data-testid={`nav-link-${item.key}`}
              className="text-sm font-medium text-slate-700 transition-colors hover:text-[#0B2545]"
            >
              {t(`header.${item.key}`)}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={switchLang}
            data-testid="header-language-switch"
            aria-label={t("header.language")}
            className="hidden rounded-md border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:border-[#0B2545] hover:text-[#0B2545] sm:inline-flex"
          >
            {lang === "mx" ? "EN" : "ES-MX"}
          </button>

          <Button
            type="button"
            onClick={() => goToSection("#contact")}
            data-testid="header-cta-button"
            className="hidden bg-[#0D9488] text-white hover:bg-[#0F766E] md:inline-flex"
          >
            {t("header.cta")}
          </Button>

          {/* Mobile hamburger */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                aria-label={t("header.open_menu")}
                data-testid="header-mobile-menu-trigger"
                className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-slate-300 text-slate-700 md:hidden"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              data-testid="header-mobile-menu"
              className="w-72 bg-white"
            >
              <SheetHeader>
                <SheetTitle className="font-serif text-slate-900">
                  {t("header.menu_title")}
                </SheetTitle>
              </SheetHeader>
              <div className="mt-6 flex flex-col gap-1">
                {NAV.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => goToSection(item.hash)}
                    data-testid={`mobile-nav-link-${item.key}`}
                    className="rounded-md px-3 py-2 text-left text-base font-medium text-slate-800 hover:bg-slate-100"
                  >
                    {t(`header.${item.key}`)}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    switchLang();
                  }}
                  data-testid="mobile-language-switch"
                  className="mt-4 rounded-md border border-slate-300 px-3 py-2 text-left text-sm font-semibold text-slate-700"
                >
                  {t("header.language")}: {lang === "mx" ? "EN" : "ES-MX"}
                </button>
                <button
                  type="button"
                  onClick={() => goToSection("#contact")}
                  data-testid="mobile-cta-button"
                  className="mt-2 rounded-md bg-[#0D9488] px-3 py-2 text-center text-sm font-semibold text-white hover:bg-[#0F766E]"
                >
                  {t("header.cta")}
                </button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
